/* eslint-disable import/prefer-default-export */
import { throttle } from 'lodash';
import { useIntl } from 'react-intl';
import { useMemo, useEffect, useRef, useState, useLayoutEffect, useContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FlagsContext } from 'react-unleash-flags';
import { useSelector } from 'react-redux';

import { useAppDispatch } from './store';

import { selectDealershipId } from 'services/Authentication/selectors';
import { selectHierarchyValues, selectSelectedHierarchyEntity } from 'services/Hierarchy/selectors';

import { setSelectedEntity } from 'services/Hierarchy/actions';
import { Option } from 'components/TreeSelect';

export const useServiceWorkerMessage = (callback, { scope = null, event = null } = {}) => {
  const [sw, setSW] = useState();

  useEffect(() => {
    if (navigator.serviceWorker) {
      // @ts-expect-error TS(2304): Cannot find name 'navigator'.
      setSW(navigator.serviceWorker);
    }
  }, [setSW]);

  useEffect(() => {
    if (sw) {
      let actualCallback;

      if (callback) {
        if (scope !== null && event !== null) {
          actualCallback = (listenerEvent) => {
            if (listenerEvent.data.scope === scope && listenerEvent.data.event === event) {
              callback(listenerEvent.data);
            }
          };
          // @ts-expect-error TS(2339): Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
          sw.addEventListener('message', actualCallback);
          // @ts-expect-error TS(2339): Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
          return () => sw.removeEventListener('message', actualCallback);
        } else if (scope !== null) {
          // listen for only specific scope

          actualCallback = (listenerEvent) => {
            if (listenerEvent.data.scope === scope) {
              callback(listenerEvent.data);
            }
          };
          // @ts-expect-error TS(2339): Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
          sw.addEventListener('message', actualCallback);
          // @ts-expect-error TS(2339): Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
          return () => sw.removeEventListener('message', actualCallback);
        } else {
          // listen for all
          // @ts-expect-error TS(2339): Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
          sw.addEventListener('message', (listenerEvent) => callback(listenerEvent.data));
          // @ts-expect-error TS(2339): Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
          return () => sw.removeEventListener('message', (listenerEvent) => callback(listenerEvent.data));
        }
      }
    }
    return undefined;
  }, [callback, sw, scope, event]);

  if (sw) {
    // @ts-expect-error TS(2339): Property 'controller' does not exist on type 'neve... Remove this comment to see the full error message
    return sw.controller;
  } else if (!process.env.TEST) {
    // eslint-disable-next-line no-console
    console.warn('Service Worker unavailable, are you using HTTPS?');
  }
  return null;
};

export const useAboveBelowViewport = ({ offsetTop = 0, offsetBottom = 0 }) => {
  const itemRef = useRef(null);

  let rect = itemRef.current?.getBoundingClientRect();
  const isAboveView = () => rect && rect.top + rect.height / 2 < 0 + offsetTop;
  const isBelowView = () => rect && rect.bottom - rect.height / 2 > window.innerHeight - offsetBottom;

  const [position, setPosition] = useState({
    visible: null,
    aboveViewport: null,
    belowViewport: null,
  }); // Not rendered yet

  const handleScroll = () => {
    rect = itemRef.current?.getBoundingClientRect();
    if (isAboveView() && !position.aboveViewport) {
      setPosition({
        visible: false,
        aboveViewport: true,
        belowViewport: false,
      });
    } else if (isBelowView() && !position.belowViewport) {
      setPosition({
        visible: false,
        aboveViewport: false,
        belowViewport: true,
      });
    } else if (!isAboveView() && !isBelowView() && !position.visible) {
      setPosition({
        visible: true,
        aboveViewport: false,
        belowViewport: false,
      });
    }
  };

  // Effect to get position after the render
  useLayoutEffect(() => {
    handleScroll();
  }, []);

  // Effect to add scroll event listener on first render
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { ...position, ref: itemRef };
};

/**
 * Hook to get the previous value of a variable.
 * return current value on initial render.
 *
 * @param value The value to get the previous value of.
 * @returns The previous value of the variable.
 */
export const usePrevious = <T>(value: T): T => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export function runThrottledCallbackOnNavigation(
  throttledCallback: () => void,
  previousPathname: string,
  pathname: string
) {
  if (previousPathname !== pathname) {
    throttledCallback();
  }
}

/**
 * This hook will call the provided `callback` when the user navigates to a new page.
 * It will only trigger the `callback` if the last trigger was more than `minimumInterval` milliseconds ago.
 */
export function useThrottledCallbackOnNavigation(callback: () => void, minimumInterval: number): void {
  const { pathname } = useLocation();
  const previousPathname = usePrevious(pathname);

  const throttledCallback = useMemo(() => throttle(callback, minimumInterval), [callback, minimumInterval]);

  useEffect(() => {
    runThrottledCallbackOnNavigation(throttledCallback, previousPathname, pathname);
  }, [pathname, previousPathname, throttledCallback]);
}

/**
 * This hook will refresh the feature flags when the user navigates to a new page.
 * It will only refresh the data if the last refresh was more than `minimumInterval` milliseconds ago.
 * @param minimumInterval The minimum interval in milliseconds between refreshes. default is 30 minutes.
 */
export const useFeatureFlagRefresh = (minimumInterval = 30 * 60 * 1000) => {
  const flagClient = useContext(FlagsContext);

  const flagsRefresh = useCallback(() => {
    if (flagClient) {
      flagClient.init();
    }
  }, [flagClient]);

  useThrottledCallbackOnNavigation(flagsRefresh, minimumInterval);
};

enum HierarchyType {
  SELF = 'self',
  HIERARCHY = 'hierarchy',
}

type HierarchyOptions = {
  showAllEntitiesOptions?: boolean;
};

export const useHierarchy = ({ showAllEntitiesOptions = false }: HierarchyOptions = {}) => {
  const dispatch = useAppDispatch();
  const { replace } = useHistory<{ hierarchyEntity?: HierarchyType | number }>();
  const { formatMessage } = useIntl();

  const dealershipId = useSelector(selectDealershipId());
  const baseHierarchy = useSelector(selectHierarchyValues()).map((item) => ({
    value: item.id,
    label: item.name,
    parent:
      showAllEntitiesOptions && item.parentId && item.parentId === dealershipId
        ? HierarchyType.HIERARCHY
        : item.parentId,
  }));
  const currentlySelectedEntity = useSelector(selectSelectedHierarchyEntity());

  const previouslySelectedEntity = usePrevious(currentlySelectedEntity);

  const hierarchy: Option[] = [];

  if (showAllEntitiesOptions) {
    hierarchy.push({
      value: HierarchyType.HIERARCHY,
      label: formatMessage({ id: 'hierarchy.hierarchy' }),
      parent: null,
    });
  }

  hierarchy.push(
    ...[
      {
        value: HierarchyType.SELF,
        label: formatMessage({ id: 'hierarchy.self' }),
        parent: showAllEntitiesOptions ? HierarchyType.HIERARCHY : null,
        onAncestorLevel: showAllEntitiesOptions,
      },
      ...baseHierarchy,
    ]
  );

  const selectHierarchyEntity = (value: HierarchyType | number) => {
    dispatch(setSelectedEntity(value));
    replace({ state: { hierarchyEntity: value } });
  };

  return {
    hierarchy,
    selectHierarchyEntity,
    currentlySelectedEntity,
    previouslySelectedEntity,
    hasChildren: baseHierarchy.size > 0,
  };
};

export const useLocalStorageState = <T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [state, setState] = useState<T>(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
  });

  useEffect(() => {
    // Listen for storage events to update state when the item changes in localStorage.
    // Typically from changes in another tab or window
    const listener = () => {
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null) {
        setState(JSON.parse(storedValue));
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key]);

  useEffect(() => {
    // If the item doesn't exist in localStorage, set it
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, JSON.stringify(state));
    }
  }, [key, state]);

  return [state, setState];
};
